import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@material-ui/core";

import Header from "components/Public/Header";
import Footer from "components/Public/Footer";
import Button from "components/CustomButtons/Button";
import { Card, CardHeader, CardBody } from "components/Card";
import AccountDeletionService, {
  AccountDeletionError,
} from "Services/AccountDeletionService";

const PAGE_TITLE =
  "Eliminar cuenta – Encuesta App (Mi Encuesta) | Solicitud de eliminación de datos";

// TODO(owner): replace with the real support/privacy contact address.
const CONTACT_EMAIL = "soporte@miencuesta.app";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AccountDeletion(): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; confirm?: string }>(
    {}
  );
  const [submitError, setSubmitError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const validate = () => {
    const next: { email?: string; confirm?: string } = {};
    if (!email.trim()) next.email = "El correo electrónico es obligatorio";
    else if (!EMAIL_REGEX.test(email.trim()))
      next.email = "Ingresa un correo electrónico válido";
    if (!confirm)
      next.confirm =
        "Debes confirmar que entiendes que la acción es irreversible";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending || !validate()) return;
    setSubmitError("");
    setSending(true);
    try {
      await AccountDeletionService.create({
        email: email.trim(),
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        reason: reason.trim() || undefined,
        confirm: true,
      });
      setSent(true);
    } catch (e) {
      const error = e as AccountDeletionError;
      if (error.status === 400)
        setSubmitError(
          error.message ||
            "Algunos datos no son válidos. Revisa el formulario e inténtalo de nuevo."
        );
      else if (error.status === 429)
        setSubmitError(
          error.message ||
            "Has enviado demasiadas solicitudes. Espera unos minutos e inténtalo de nuevo."
        );
      else
        setSubmitError(
          error.message ||
            `No pudimos enviar tu solicitud. Inténtalo más tarde o escríbenos a ${CONTACT_EMAIL}.`
        );
    } finally {
      setSending(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <div className="py-4 pt-md-6 py-md-5 bg-black bg-op-3"></div>
      <div className="py-4 pt-md-6 py-md-5 bg-primary-faded bg-op-1">
        <Container maxWidth="md">
          <Card>
            <CardHeader>
              <h1 className="h3 text-primary">
                Solicitud de eliminación de cuenta – Encuesta App (Mi Encuesta)
              </h1>
            </CardHeader>
            <CardBody>
              <p>
                Desde esta página puedes solicitar que eliminemos tu cuenta de
                la aplicación <strong>Encuesta App</strong> (Mi Encuesta,
                desarrollada por Karimnot Inc) y los datos asociados a ella. No
                necesitas iniciar sesión.
              </p>

              <h2 className="h5 mt-4">¿Cómo funciona?</h2>
              <ol>
                <li>Llena el formulario de esta página.</li>
                <li>
                  Verificamos que la solicitud corresponda al titular de la
                  cuenta.
                </li>
                <li>
                  Eliminamos tu cuenta y tus datos en un plazo máximo de 30
                  días.
                </li>
                <li>
                  Te enviamos un correo electrónico confirmando la eliminación.
                </li>
              </ol>

              {/*
                TODO(owner): confirm the exact data-retention wording with
                legal before publishing (which data is kept, whether survey
                responses are anonymized, and the retention period).
              */}
              <h2 className="h5 mt-4">Datos que se eliminan</h2>
              <ul>
                <li>
                  Datos de tu cuenta: nombre, correo electrónico, teléfono y
                  contraseña.
                </li>
                <li>Tu perfil de usuario.</li>
              </ul>

              <h2 className="h5 mt-4">Datos que pueden conservarse</h2>
              <ul>
                <li>
                  Respuestas a encuestas que ya hayas enviado a las
                  organizaciones que las realizaron, las cuales pueden
                  conservarse de forma anonimizada.
                </li>
                <li>
                  Registros que estemos obligados a conservar por ley, durante
                  un máximo de 90 días o el plazo que exija la legislación
                  aplicable.
                </li>
              </ul>

              <p className="mt-4">
                Si prefieres, también puedes solicitar la eliminación
                escribiendo a{" "}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> desde el
                correo registrado en tu cuenta.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="h4 text-primary">Formulario de solicitud</h2>
            </CardHeader>
            <CardBody>
              {sent ? (
                <div role="status">
                  <h3 className="h5 text-success">
                    Recibimos tu solicitud de eliminación de cuenta.
                  </h3>
                  <p>
                    La revisaremos y, una vez verificada, eliminaremos tu cuenta
                    y tus datos en un plazo máximo de 30 días. Te enviaremos una
                    confirmación a <strong>{email.trim()}</strong>.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        required
                        type="email"
                        name="email"
                        inputProps={{ maxLength: 254 }}
                        label="Correo electrónico de la cuenta"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        helperText={errors.email || " "}
                        error={!!errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        type="text"
                        name="name"
                        inputProps={{ maxLength: 255 }}
                        label="Nombre (opcional)"
                        autoComplete="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        type="tel"
                        name="phone"
                        inputProps={{ maxLength: 50 }}
                        label="Teléfono (opcional)"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        name="reason"
                        inputProps={{ maxLength: 2000 }}
                        label="Motivo (opcional)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            name="confirm"
                            required
                            checked={confirm}
                            onChange={(e) => setConfirm(e.target.checked)}
                          />
                        }
                        label="Entiendo que esta acción es irreversible"
                      />
                      <FormHelperText error={!!errors.confirm}>
                        {errors.confirm || " "}
                      </FormHelperText>
                    </Grid>
                    {submitError && (
                      <Grid item xs={12}>
                        <p className="text-danger" role="alert">
                          {submitError}
                        </p>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button type="submit" color="danger" disabled={sending}>
                        {sending ? "Enviando…" : "Solicitar eliminación"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
}
